import type { PricingCardProps } from "./PricingCard";

export type PricingPlan = "free" | "individual" | "studio" | "enterprise";

type PricingCardConfig = Omit<PricingCardProps, "ctaSlot" | "className" | "children">;

export const PRICING_CONFIGS: Record<PricingPlan, PricingCardConfig> = {
  free: {
    planName: "Free",
    planBadgeVariant: "default",
    price: "$0",
    pricePeriod: "/month",
    features: [
      {
        category: "Projects",
        items: [{ text: "Up to 2 projects" }, { text: "macOS, iPadOS, Windows and visionOS" }],
      },
      {
        category: "Storage",
        items: [{ text: "Cloud storage" }],
      },
      {
        category: "Import",
        items: [{ text: "XT, STEP, IGES, DWG, STL" }],
      },
      {
        category: "Export",
        items: [{ text: "Basic resolution 3MF, STL" }],
      },
      {
        category: "Generative Tools",
        items: [
          {
            text: "Up to 3 per day – Context-rich renders with a single prompt",
          },
        ],
      },
    ],
  },

  individual: {
    planName: "Solo",
    planBadgeVariant: "blue",
    price: "$25",
    pricePeriod: "/month",
    subtitle: "$299 Billed annually, excluding VAT",
    tagline: "Everything included in Free, plus:",
    highlighted: true,
    features: [
      {
        category: "Projects",
        items: [
          { text: "Up to 100 projects" },
          {
            text: "Up to 2 Contributors per team to share and review team projects with stakeholders",
          },
          { text: "1 year version history" },
        ],
      },
      {
        category: "Storage",
        items: [{ text: "Cloud storage" }],
      },
      {
        category: "Import",
        items: [{ text: "SLDPRT, SLDASM" }],
      },
      {
        category: "Export",
        items: [{ text: "High resolution 3MF, STL" }, { text: "XT, STEP, IGES" }],
      },
      {
        category: "Generative Tools",
        items: [
          {
            text: "Up to 50 generations per day – Context-rich renders with a single prompt",
          },
        ],
      },
      {
        category: "Collaboration & Documentation",
        items: [
          { text: "Technical drawings for straightforward manufacturing hand-offs" },
          { text: "Real-time renders with 100+ materials" },
          { text: "Integrated AR on iPad for instant digital prototyping" },
          {
            text: "Secure review links for sharing 3D models, AR preview, and technical drawings",
          },
        ],
      },
      {
        category: "Onboarding & Training",
        items: [{ text: "Basic technical support" }],
      },
    ],
    seatTypes: [{ label: "Contributor", color: "orange" }],
  },

  studio: {
    planName: "Studio",
    planBadgeVariant: "default",
    price: "$37",
    pricePeriod: "/month",
    subtitle: "$449 Billed annually, excluding VAT",
    tagline: "Everything included in Solo, plus:",
    features: [
      {
        category: "Projects",
        items: [
          { text: "Unlimited shared team projects" },
          { text: "Up to 10 Editor seats" },
          {
            text: "Up to 2 Contributors per team to share and review team projects with stakeholders",
          },
          { text: "Unlimited draft projects for all Editors" },
          { text: "3 year version history" },
        ],
      },
      {
        category: "Storage",
        items: [{ text: "Cloud storage" }],
      },
      {
        category: "Import",
        items: [{ text: "NX, JT, CV5, CREO, Solid Edge, Rhino" }],
      },
      {
        category: "Export",
        items: [{ text: "JT, ACIS, FBX, Parasolid, PRC, Universal 3D, VRML" }],
      },
      {
        category: "Generative Tools",
        items: [
          {
            text: "Up to 500 generations per day – Context-rich renders with a single prompt",
          },
        ],
      },
      {
        category: "Collaboration & Documentation",
        items: [
          { text: "Vision Pro for immersive design reviews in XR" },
          { text: "Custom material libraries for consistent brand presentations" },
          { text: "Multiple Spaces with granular access controls" },
        ],
      },
      {
        category: "Onboarding & Training",
        items: [{ text: "Priority technical support" }],
      },
    ],
    seatTypes: [
      { label: "Editor", color: "blue" },
      { label: "Contributor", color: "orange" },
    ],
  },

  enterprise: {
    planName: "Enterprise",
    planBadgeVariant: "dark",
    pricePrefix: "from ",
    price: "$125",
    pricePeriod: "/month",
    priceSubline: "per Editor seat",
    subtitle: "Contact us for an exact quote",
    tagline: "Everything included in Studio, plus:",
    features: [
      {
        category: "Projects",
        items: [
          {
            text: "Granular access control for multiple teams, designed for larger organizations",
            icon: "dot",
          },
          { text: "Unlimited version history", icon: "dot" },
        ],
      },
      {
        category: "Storage",
        items: [
          { text: "Company ownership of all data", icon: "dot" },
          {
            text: "Flexible data storage options",
            icon: "dot",
            subItems: ["Cloud off", "Single tenant", "Private cloud", "On-premise"],
          },
        ],
      },
      {
        category: "Generative Tools",
        items: [
          {
            text: "Unlimited generations – Context-rich renders with a single prompt",
            icon: "dot",
          },
        ],
      },
      {
        category: "Collaboration & Documentation",
        items: [
          {
            text: "Multi-device Vision Pro Sessions – remotely or in person",
            icon: "dot",
          },
        ],
      },
      {
        category: "Onboarding & Training",
        items: [
          {
            text: "Accelerated enterprise onboarding with customized trainings and dedicated account management",
            icon: "dot",
          },
        ],
      },
      {
        category: "Security and Compliance",
        items: [{ text: "ISO 27001 certified, SOC 2 Type II", icon: "dot" }],
      },
    ],
    seatTypes: [
      { label: "Editor", color: "blue" },
      { label: "Contributor", color: "orange" },
    ],
  },
};
